import { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile widget (Technical Blueprint Section 8.4 abuse control). The Owner has not created a
 * site key yet, so this renders nothing and reports itself "satisfied" whenever no site key is configured --
 * the upload flow must never be blocked by an env var that doesn't exist yet. Once
 * REACT_APP_TURNSTILE_SITE_KEY is set, this loads Cloudflare's script once (module-level singleton) and
 * renders the real widget; a script-load failure degrades the same way (skips, does not block upload) since
 * a broken CDN script is an availability problem, not a reason to lose an otherwise-legitimate customer.
 */

let scriptPromise = null;
function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-turnstile-loader="1"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("turnstile script failed to load")));
      return;
    }
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.dataset.turnstileLoader = "1";
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => reject(new Error("turnstile script failed to load")));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * onVerify(token|null) fires exactly once when the widget is "settled": with a real token when the challenge
 * passes, or with null immediately when no site key is configured (graceful skip) or the script fails to load
 * (graceful degrade). Consumers should treat "settled" as "upload is allowed to proceed", not "a human was
 * verified" -- the server is the actual enforcement point either way.
 */
export default function TurnstileWidget({ siteKey, onVerify, onExpire }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [status, setStatus] = useState(siteKey ? "loading" : "skipped");

  useEffect(() => {
    if (!siteKey) {
      onVerify && onVerify(null);
      return undefined;
    }
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => { setStatus("verified"); onVerify && onVerify(token); },
          "expired-callback": () => { setStatus("expired"); onExpire && onExpire(); onVerify && onVerify(null); },
          "error-callback": () => { setStatus("skipped"); onVerify && onVerify(null); },
        });
        setStatus("rendered");
      })
      .catch(() => {
        // CDN unreachable, blocked by an extension, etc. Degrade gracefully rather than block upload.
        if (!cancelled) { setStatus("skipped"); onVerify && onVerify(null); }
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* already gone */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={containerRef} data-testid="turnstile-widget" data-sitekey={siteKey} data-status={status} />;
}
